import * as React from 'react';
import { Table } from 'antd';
import * as ReactDOM from 'react-dom';
import { elementOuterHeight } from '../../../../utils/utils';
import { withSize } from 'react-sizeme';
import { createUseStyles } from 'react-jss';
import { createSelector } from 'reselect';
import classNames from 'classnames';
import CustomTableTitle from './CustomTableTitle';
import { LocalStorageService, LocalStorageKeys } from 'src/services/localStorage';
import CustomTableCell from './CustomTableCell';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { ThemeType } from 'src/theming/jssTypes';

const defaultWidth = 190;

const useStyles = createUseStyles((theme: ThemeType) => ({
  tableParent: {
    margin: theme.spacing[2],
    '& .ant-table-tbody > tr > td, .ant-table-thead > tr > th': {
      wordBreak: 'break-word',
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: theme.color.semantic.text.primary,
    },
    '& .ant-table-tbody > tr > td': {
      wordWrap: 'break-word',
      wordBreak: 'break-all',
      '& a': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-table-thead': {
      backgroundColor: theme.color.semantic.accent.cyan.subtle,
      color: theme.color.semantic.text.primary,
    },
  },
  compactTable: {
    '& .ant-table-thead > tr > th': {
      overflow: 'hidden',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.tight,
      padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
      backgroundColor: theme.color.semantic.accent.cyan.subtle,
      color: theme.color.semantic.text.primary,
      borderColor: theme.color.semantic.border.subtle,
      borderWidth: theme.border.width.sm,

      '& .ant-table-column-title': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        width: '100%',
      },

      '&:hover': {
        backgroundColor: `${theme.color.semantic.accent.cyan.subtle} !important`,
        borderColor: theme.color.semantic.border.subtle,
        '& a': {
          color: theme.color.semantic.brand.default.primary,
        },
      },

      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.accent.cyan.subtle} !important`,
        borderColor: theme.color.semantic.border.subtle,
        '& a': {
          color: theme.color.semantic.accent.cyan.subtle,
        },
      },
    },
    '& .ant-table-thead > tr': {
      backgroundColor: theme.color.semantic.background.neutral.soft,
    },
    '& .ant-table-row': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
    },
    '& .ant-table-row > td': {
      fontSize: theme.typography.fontSize.sm,
      lineHeight: theme.typography.lineHeight.tight,
      padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
      color: theme.color.semantic.text.primary,
      fontWeight: theme.typography.fontWeight.regular,
      borderColor: theme.color.semantic.border.subtle,
      borderWidth: theme.border.width.sm,

      '& a': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-checkbox-indeterminate .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      '&:after': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        border: 'none',
        height: theme.border.width.md,
        width: theme.spacing[2],
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
    },
    '& .ant-table-expanded-row > td': {
      padding: `${theme.spacing[2]} ${theme.spacing[0]}`,
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: theme.color.semantic.text.primary,
      borderColor: theme.color.semantic.border.subtle,

      '& a': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-table-expanded-row:hover > td': {
      backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
      borderColor: theme.color.semantic.border.subtle,

      '& a': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-table-expanded-row:active > td, & .ant-table-expanded-row:focus > td': {
      backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
      borderColor: theme.color.semantic.border.subtle,

      '& a': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-table-row-selected': {
      backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
      '& .ant-table-cell-fix-left, .ant-table-cell-fix-right': {
        backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
    },
    '& .ant-table-row-selected > td': {
      backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,

      '&:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
      },
    },
    '& .ant-table-ping-left .ant-table-cell-fix-left-first::after, .ant-table-ping-left .ant-table-cell-fix-left-last::after': {
      boxShadow: 'none',
    },
    '& .ant-table-ping-right .ant-table-cell-fix-right-first::after, .ant-table-ping-right .ant-table-cell-fix-right-last::after': {
      boxShadow: 'none',
    },
    '& .ant-table-cell-row-hover': {
      backgroundColor: `${theme.color.semantic.accent.cyan.subtle} !important`,
      '& a': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-table-placeholder .ant-table-expanded-row-fixed': {
      width: '100% !important',
      minWidth: '100% !important',
      fontSize: theme.typography.fontSize.sm,
    },
    // Prevent hover styles on empty table placeholder
    '& .ant-table-placeholder .ant-table-cell': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
    },
  },
}));

class FixedHeaderTableInner extends React.PureComponent<any, any> {
  state: any = {
    scrollY: 0,
    scrollX: 0,
    columnWidths: {},
  };
  tableRef;
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
  }

  getLocalStorageKeyForTable() {
    if (this.tableRef.current) {
      const pathname = this.props.match?.path ?? window.location.pathname;
      const currentTableRef = this.tableRef.current;
      const tabParent = currentTableRef.closest ?
      currentTableRef.closest('.ant-tabs-tabpane, .shipsy-generic-tabs-with-sm-cls') : null;
      let idxKey = tabParent?.getAttribute('id');
      if (!idxKey) {
        const allTableRefs = Array.from(document.getElementsByClassName('shipsy-fixed-header-table') ?? []);
        const currentTableIndex = currentTableRef ?
        allTableRefs.findIndex(ref => ref === currentTableRef) : -1;
        if (currentTableIndex !== null && currentTableIndex > -1) {
          idxKey = currentTableIndex;
        }
      }
      if (idxKey && pathname) {
        return `${LocalStorageKeys._TABLE_COLUMS_WIDTH_DATA}:${pathname}-${idxKey}`;
      }
    }
    return null;
  }

  fetchColumnWidthFromLocalStorage() {
    let columnWidthFromLocalStorage;
    const key = this.getLocalStorageKeyForTable();
    if (key) {
      try {
        const localdata = LocalStorageService.getRaw(key);
        if (localdata) {
          columnWidthFromLocalStorage = JSON.parse(localdata);
        }
      } catch (e) {
        console.log(e);
      }
    }
    return columnWidthFromLocalStorage;
  }

  getColumnData() {
    const { columns, size, resizable = true, minHeight, enableTextWrap = false } = this.props;
    const height = size.height || minHeight;
    const containerWidth = size.width;

    return columns.map((column, index) => {
      const widthKey = column.key ?? column.dataIndex;

      // Convert percentage widths to pixels for resizing to work
      let columnWidth = this.state.columnWidths[widthKey] ?? column.width;
      if (typeof columnWidth === 'string' && columnWidth.endsWith('%') && containerWidth) {
        const percentage = parseFloat(columnWidth);
        columnWidth = Math.floor((percentage / 100) * containerWidth);
      }

      return {
        ...column,
        onHeaderCell: (column) => ({
          width: columnWidth,
          tableHeight: height,
          onResizeStop: this.handleResize(column, index),
          resizable,
          enableTextWrap,
        }),
        onCell: (record) => ({
          sameTooltipAsElem: column.sameTooltipAsElem,
          enableTextWrap,
        }),
        width: columnWidth,
      };
    });
  }

  getScrollHeight = () => {
    const { size, minHeight } = this.props;
    const { width } = size;
    const height = size.height || minHeight;
    const tableElement: any = ReactDOM
      .findDOMNode(this.tableRef.current);

    if (!tableElement || !height || !width) {
      return 0;
    }

    const tableHeaderElementHeight = tableElement
      .getElementsByTagName('thead')[0]
      .offsetHeight;

    const paginationElement = tableElement
      .getElementsByClassName('ant-table-pagination')[0];
    // Pagination might not always be present
    const paginationElementHeight = paginationElement ? elementOuterHeight(paginationElement) : 0;

    // Offset of position of title (equal to 1 (top: 1 in ant css))
    const titlePositionOffset = 1;

    const tableTitleElementHeight = tableElement
      .getElementsByClassName('ant-table-title')[0] ?
      tableElement
        .getElementsByClassName('ant-table-title')[0]
        .offsetHeight + titlePositionOffset : 0;

    const cardHeaderHeight =
      (tableHeaderElementHeight
        + tableTitleElementHeight
        + paginationElementHeight
      );

    return Math.max(0, (height - cardHeaderHeight));
  };

  getScrollWidth = createSelector(
    (p:any) => p.columns,
    (columns:any) => {
      return (columns.reduce((acc, cur) => {
        return acc + (cur.width || defaultWidth);
      }, 0) - 15);
    },
  );

  getTotalColumnWidth = createSelector(
    (p:any) => p.columns,
    (columns:any) => {
      return (columns.reduce((acc, cur) => {
        return acc + (cur.width || defaultWidth);
      }, 0));
    },
  );

  componentDidMount() {
    const newState: any = {
      columnWidths: this.fetchColumnWidthFromLocalStorage() || {},
    };
    const scrollY = this.getScrollHeight();
    if (scrollY !== this.state.scrollY) {
      newState.scrollY = scrollY;
    }

    this.setState(newState);
  }

  componentDidUpdate() {
    const scrollY = this.getScrollHeight();
    if (scrollY !== this.state.scrollY) {
      this.setState({ scrollY });
    }
  }

  handleResize(column: any, index: number) {
    return (width) => {
      const { columnWidths } = this.state;
      const newColumnWidth = {...columnWidths};
      newColumnWidth[column.key ?? column.dataIndex] = Math.max(width, 36);
      const keyToSet = this.getLocalStorageKeyForTable();
      if (keyToSet) {
        LocalStorageService.set(keyToSet, JSON.stringify(newColumnWidth));
      }

      this.setState({ columnWidths: newColumnWidth });
    };
  }

  render() {
    const {
      size,
      scroll,
      classes,
      hideScrollX,
      style,
      rowSelection,
      locale,
      components,
      t,
      minHeight,
      enableTextWrap = false,
      ...otherProps
    } = this.props;
    const columns = this.getColumnData();
    const { width } = size;
    const height = size.height || minHeight;
    const scrollY = (width && height) ? this.state.scrollY : false;
    let scrollX: undefined | number;
    let columnsToSet = columns;
    if (hideScrollX) {
      // No scroll in x
      const totalColumnWidth = this.getTotalColumnWidth(this.props);
      let totalWidthToSet = 0;
      columnsToSet = columns.map((c) => {
        const widthToSet =
          Math.floor(((c.width || defaultWidth) / totalColumnWidth) * (width - 10));
        totalWidthToSet += widthToSet;
        return {...c, width: widthToSet};
      });
      scrollX = totalWidthToSet;
    } else {
      scrollX = this.getScrollWidth(this.props);
      if (scroll && scroll.x) {
        scrollX = scroll.x;
      }
      if (scrollX && scrollX < width && columns.length) {
        const givenTotalWidth = columns.reduce((acc, cur) => {
          return acc + (cur.width);
        }, 0);
        const numberOfdefinedWidthColumns = columns.reduce((acc, cur) => {
          return acc + (cur.width ? 1 : 0);
        }, 0);
        columns.forEach(c => !c.width &&
          (c['width'] = (
            width - givenTotalWidth - 62) / (columns.length - numberOfdefinedWidthColumns)));
      }
      scrollX = Math.max(scrollX || 0, width);
    }
    const scrollPropToAdd: any = {
      y: scrollY,
      x: scrollX,
    };

    return (
      <div style={{ 
        position: 'relative', 
        height: '100%', 
        minHeight,
        ...style
      }}>
        <div 
          className={classNames(
            classes.compactTable,
            classes.tableParent,
            this.props.className,
            'shipsy-fixed-header-table',
          )}
          style={{ 
            position: 'absolute', 
            width: '100%', 
            height: height || '100%',
            minHeight,
          }}
          ref={this.tableRef}
        >
          <Table
            {...otherProps}
            columns={columnsToSet}
            scroll={scrollPropToAdd}
            bordered={true}
            locale={{
              ...(locale || {}),
              emptyText: locale?.emptyText ?? t('No data found'),
            }}
            components={{
              header: {
                cell: CustomTableTitle,
              },
              body: {
                cell: CustomTableCell,
              },
            }}
            rowSelection={rowSelection ? {
              ...rowSelection,
              getCheckboxProps: (record) => {
                const propsFromParent = rowSelection.getCheckboxProps ?
                rowSelection.getCheckboxProps(record) : {};
                return {
                  ...propsFromParent,
                  style: {
                    borderRadius: '2px',
                  },
                };
              },
            } : null}
          />
        </div>
      </div>
    );
  }
}

const FixedHeaderTable: React.FC<any> = React.memo((props) => {
  const classes = useStyles();
  return <FixedHeaderTableInner {...props} classes={classes} />;
});

export default withTranslation()(React.memo(
  withSize({ monitorHeight: true })(
    withRouter(FixedHeaderTable as any),
  ),
));
